import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { DashboardHeader } from '../Dashboard/DashboardUi';
import { useAuth } from '../../context/AuthContext';
import { equipeApi } from '../../lib/equipeApi';
import { adminApi } from '../../lib/api';
import { DroitsInsuffisants } from '../Organisation/EtatsPage';
import { ComptesPage } from './ComptesPage';
import { ComptePage } from './ComptePage';
import { JournalPage } from './JournalPage';

/**
 * Coque du module « Mon équipe » (maquette L02, refonte 2026-10-29) : en-tête, deux onglets (Personnes · Historique), contexte partagé.
 * Les droits et les données sensibles se règlent sur la fiche de chaque personne ; les demandes d'accès sont un bandeau de la liste.
 * Monté deux fois : `/equipe/*` (direction) et `/admin/clients/:id/equipe/*` (super-admin, qui garde la main).
 */
interface Cible { api: ReturnType<typeof equipeApi>; base: string; clientId?: string }
const Ctx = createContext<Cible | null>(null);
export const useEquipe = () => {
    const v = useContext(Ctx);
    if (!v) throw new Error('useEquipe doit être utilisé dans EquipeShell');
    return v;
};

const ONGLETS = [
    { to: '', label: 'Personnes', fin: true },
    { to: '/journal', label: 'Historique' },
];

const Shell: React.FC<{ clientId?: string; base: string }> = ({ clientId, base }) => {
    const { user } = useAuth();
    const [nom, setNom] = useState('');
    const api = useMemo(() => equipeApi(clientId), [clientId]);
    useEffect(() => {
        if (!clientId) return undefined;
        let vivant = true;
        adminApi.getClient(clientId).then((c: any) => { if (vivant) setNom(c?.company_name || c?.name || ''); }).catch(() => {});
        return () => { vivant = false; };
    }, [clientId]);

    if (user?.type === 'collaborator' || user?.type === 'employe') {
        return <div className="max-w-[900px]"><DroitsInsuffisants /></div>;
    }
    return (
        <Ctx.Provider value={{ api, base, clientId }}>
            <div className="space-y-4 max-w-[1100px]">
                {clientId && <div role="note" className="rounded-xl bg-state-info-soft text-state-info px-4 py-3 text-[13px]">Vous gérez l'équipe de <b>{nom || clientId}</b> en tant que super-admin.</div>}
                <DashboardHeader societe={clientId ? '' : user?.company_name || ''} titre="Mon équipe" sousTitre="Les personnes qui travaillent avec vous, et ce que chacune peut faire." />
                <nav aria-label="Sections de l'équipe" className="flex gap-2 overflow-x-auto pb-1">
                    {ONGLETS.map((o) => (
                        <NavLink key={o.to} to={`${base}${o.to}`} end={o.fin}
                            className={({ isActive }) => `h-11 md:h-8 min-w-[44px] md:min-w-0 px-3.5 inline-flex items-center rounded-full border text-[12.5px] font-semibold whitespace-nowrap shrink-0 ${isActive ? 'bg-content text-surface border-transparent' : 'bg-surface text-content border-border hover:border-content'}`}>
                            {o.label}
                        </NavLink>
                    ))}
                </nav>
                <Routes>
                    <Route index element={<ComptesPage />} />
                    <Route path="journal" element={<JournalPage />} />
                    <Route path="comptes/:id" element={<ComptePage />} />
                    {/* Anciennes adresses (matrice, données sensibles, demandes) : tout se règle maintenant depuis la liste et les fiches. */}
                    <Route path="*" element={<Navigate to={base} replace />} />
                </Routes>
            </div>
        </Ctx.Provider>
    );
};

export const EquipeRoutes: React.FC = () => <Shell base="/equipe" />;
export const AdminEquipeRoutes: React.FC = () => {
    const { id = '' } = useParams();
    return <Shell clientId={id} base={`/admin/clients/${id}/equipe`} />;
};
